import { memo } from "react";
import {
  ArrowDownTrayIcon,
  HandRaisedIcon,
  LockOpenIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  PlusCircleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import { Characteristic } from "@sablier/v2-components/molecules";
import { ActionCategory } from "@sablier/v2-constants";
import { useResolvedENS, useShortAddress } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Action } from "@sablier/v2-models";
import { useToken } from "~/client/hooks";
import type { ComponentProps } from "react";
import Template from "./template";

type Description = ComponentProps<typeof Characteristic>["description"];

interface Props {
  action: Action;
  isUnsupportedAllowed?: boolean;
}

function Approval({ action }: Props) {
  const { t } = useT();
  const description: Description = [];

  const to = action.addressB;
  const shortTo = useShortAddress(to);

  description.push(
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.approve.allowance"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortTo,
        toCopy: to,
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.approve.single.modify"),
      },
    },
  );

  return (
    <Template
      action={action}
      description={description}
      icon={LockOpenIcon}
      title={t("modalCards.history.approve.single.title")}
    />
  );
}
function ApprovalForAll({ action }: Props) {
  const description: Description = [];
  const { t } = useT();

  const from = action.addressA;
  const to = action.addressB;
  const shortFrom = useShortAddress(from);
  const shortTo = useShortAddress(to);

  description.push(
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.approve.allowance"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortTo,
        toCopy: to,
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.approve.multiple.modify", {
          status: `(${
            action.amountA.humanized.isZero() ? t("words.off") : t("words.on")
          })`,
        }),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortFrom,
        toCopy: from,
      },
    },
  );

  return (
    <Template
      action={action}
      description={description}
      icon={LockOpenIcon}
      title={t("modalCards.history.approve.multiple.title")}
    />
  );
}

function Create({ action }: Props) {
  const description: Description = [];
  const { t } = useT();

  const token = useToken({ token: action.token });
  const chainId = action.chainId;

  const from = action.addressA;
  const to = action.addressB;
  const shortFrom = useShortAddress(from);
  const shortTo = useShortAddress(to);

  const { data: ensFrom } = useResolvedENS({
    address: from,
    chainId,
  });
  const { data: ensTo } = useResolvedENS({
    address: to,
    chainId,
  });

  description.push(
    {
      purpose: "content",
      options: {
        value: _.capitalize(t("words.sender")),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortFrom,
        toCopy: from,
      },
    },
    ...(ensFrom
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensFrom})`,
            },
          },
        ]
      : []),
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.create.created"),
      },
    },
    {
      purpose: "amount",
      options: {
        token,
        value: action.amountA?.humanized.toString(),
      },
    },
    {
      purpose: "content",
      options: {
        value: t("words.towards"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortTo,
        toCopy: to,
      },
    },
    ...(ensTo
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensTo})`,
            },
          },
        ]
      : []),
  );

  return (
    <Template
      action={action}
      description={description}
      icon={PlusCircleIcon}
      title={t("modalCards.history.create.title")}
    />
  );
}

function Renounce({ action }: Props) {
  const description: Description = [];
  const { t } = useT();

  description.push({
    purpose: "content",
    options: {
      value: t("modalCards.history.renounce.description"),
    },
  });

  return (
    <Template
      action={action}
      description={description}
      icon={HandRaisedIcon}
      title={t("modalCards.history.renounce.title")}
    />
  );
}

function Cancel({ action }: Props) {
  const description: Description = [];
  const { t } = useT();

  const token = useToken({ token: action.token });
  const chainId = action.chainId;

  const from = action.addressA;
  const to = action.addressB;
  const shortFrom = useShortAddress(from);
  const shortTo = useShortAddress(to);

  const { data: ensFrom } = useResolvedENS({
    address: from,
    chainId,
  });
  const { data: ensTo } = useResolvedENS({
    address: to,
    chainId,
  });

  description.push(
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.cancel.done"),
      },
    },
    {
      purpose: "amount",
      options: {
        token,
        value: action.amountB?.humanized.toString(),
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.cancel.already"),
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.cancel.sent"),
      },
    },

    {
      purpose: "content",
      options: {
        value: t("modalCards.history.cancel.recipient"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortTo,
        toCopy: to,
      },
    },
    ...(ensTo
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensTo})`,
            },
          },
        ]
      : []),
    {
      purpose: "content",
      options: {
        value: `.`,
      },
    },
    {
      purpose: "amount",
      options: {
        token,
        value: action.amountA?.humanized.toString(),
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.cancel.unstreamed"),
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.cancel.refunded"),
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.cancel.sender"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortFrom,
        toCopy: from,
      },
    },
    ...(ensFrom
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensFrom})`,
            },
          },
        ]
      : []),
  );

  return (
    <Template
      action={action}
      description={description}
      icon={NoSymbolIcon}
      title={t("modalCards.history.cancel.title")}
    />
  );
}

function Transfer({ action }: Props) {
  const { t } = useT();
  const description: Description = [];
  const chainId = action.chainId;

  const from = action.addressA;
  const to = action.addressB;
  const shortFrom = useShortAddress(from);
  const shortTo = useShortAddress(to);

  const { data: ensFrom } = useResolvedENS({
    address: from,
    chainId,
  });
  const { data: ensTo } = useResolvedENS({
    address: to,
    chainId,
  });

  description.push(
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.transfer.recipient"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortFrom,
        toCopy: from,
      },
    },
    ...(ensFrom
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensFrom})`,
            },
          },
        ]
      : []),
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.transfer.to"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortTo,
        toCopy: to,
      },
    },
    ...(ensTo
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensTo})`,
            },
          },
        ]
      : []),
  );

  return (
    <Template
      action={action}
      description={description}
      icon={PaperAirplaneIcon}
      title={t("modalCards.history.transfer.title")}
    />
  );
}

function Withdraw({ action }: Props) {
  const description: Description = [];
  const { t } = useT();

  const token = useToken({ token: action.token });
  const chainId = action.chainId;

  const from = action.addressA;
  const to = action.addressB;
  const shortFrom = useShortAddress(from);
  const shortTo = useShortAddress(to);

  const { data: ensFrom } = useResolvedENS({
    address: from,
    chainId,
  });
  const { data: ensTo } = useResolvedENS({
    address: to,
    chainId,
  });

  description.push(
    {
      purpose: "amount",
      options: {
        token,
        value: action.amountB?.humanized.toString(),
      },
    },
    {
      purpose: "content",
      options: {
        value: t("modalCards.history.withdraw.by"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortFrom,
        toCopy: from,
      },
    },
    ...(ensFrom
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensFrom})`,
            },
          },
        ]
      : []),

    {
      purpose: "content",
      options: {
        value: t("words.to"),
      },
    },
    {
      purpose: "copy",
      options: {
        value: shortTo,
        toCopy: to,
      },
    },
    ...(ensTo
      ? [
          {
            purpose: "content" as const,
            options: {
              value: `(${ensTo})`,
            },
          },
        ]
      : []),
  );

  return (
    <Template
      action={action}
      description={description}
      icon={ArrowDownTrayIcon}
      title={t("modalCards.history.withdraw.title")}
    />
  );
}

function Item({ action, isUnsupportedAllowed = false }: Props) {
  const { t } = useT();

  switch (action.category) {
    case ActionCategory.APPROVAL:
      return <Approval action={action} />;
    case ActionCategory.APPROVAL_FOR_ALL:
      return <ApprovalForAll action={action} />;
    case ActionCategory.CANCEL:
      return <Cancel action={action} />;
    case ActionCategory.CREATE:
      return <Create action={action} />;
    case ActionCategory.RENOUNCE:
      return <Renounce action={action} />;
    case ActionCategory.TRANSFER:
      return <Transfer action={action} />;
    case ActionCategory.WITHDRAW:
      return <Withdraw action={action} />;
    default:
      break;
  }

  return isUnsupportedAllowed ? (
    <Template
      action={action}
      description={[
        {
          purpose: "content",
          options: {
            value: t("modalCards.history.unknown"),
          },
        },
      ]}
      icon={QuestionMarkCircleIcon}
      title={t("modalCards.history.unsupported")}
    />
  ) : (
    false
  );
}

export default memo(Item);
